/**
 * Grouped bar chart. Incomplete months are drawn muted and are expected to
 * have been excluded from averages by the caller.
 *
 * Every bar carries an `a11y.chartBar` label and the whole chart has a text
 * equivalent underneath for screen readers.
 */
import { chart } from '@/lib/tokens'
import { t } from '@/lib/copy'

export interface BarSeries {
  key: string
  value: number
  color: string
  label: string
}

export interface BarDatum {
  label: string
  series: BarSeries[]
  muted?: boolean
}

export function BarChart({
  data,
  height = 200,
  formatValue,
  caption,
}: {
  data: BarDatum[]
  height?: number
  formatValue: (value: number) => string
  caption?: string
}) {
  if (data.length === 0) return null

  const width = 720
  const padBottom = 24
  const plotHeight = height - padBottom
  const max = Math.max(1, ...data.flatMap((d) => d.series.map((s) => Math.abs(s.value))))
  const groupWidth = width / data.length
  const barCount = Math.max(1, data[0].series.length)
  const barWidth = Math.max(2, (groupWidth - chart.barGap * (barCount + 1)) / barCount)

  const gridLines = [0.25, 0.5, 0.75, 1]

  return (
    <figure style={{ margin: 0 }}>
      <svg
        className="chart"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        style={{ height }}
        role="img"
        aria-label={caption}
      >
        {gridLines.map((line) => (
          <line
            key={line}
            x1={0}
            x2={width}
            y1={plotHeight - line * plotHeight}
            y2={plotHeight - line * plotHeight}
            stroke="var(--color-chartGrid)"
            strokeDasharray={chart.gridDashArray}
          />
        ))}
        <line x1={0} x2={width} y1={plotHeight} y2={plotHeight} stroke="var(--color-chartAxis)" />

        {/* Keyed by index: month labels repeat once the range crosses a year. */}
        {data.map((datum, groupIndex) => (
          <g key={groupIndex} opacity={datum.muted ? 'var(--opacity-muted)' : 1}>
            {datum.series.map((series, i) => {
              const barHeight = (Math.abs(series.value) / max) * plotHeight
              const x = groupIndex * groupWidth + chart.barGap + i * (barWidth + chart.barGap)
              return (
                <rect
                  key={series.key}
                  x={x}
                  y={plotHeight - barHeight}
                  width={barWidth}
                  height={Math.max(barHeight, 1)}
                  rx={chart.barCornerRadius}
                  fill={series.color}
                >
                  <title>{t('a11y.chartBar', { label: `${datum.label} ${series.label}`, amount: formatValue(series.value) })}</title>
                </rect>
              )
            })}
            <text
              x={groupIndex * groupWidth + groupWidth / 2}
              y={height - 6}
              textAnchor="middle"
              fill="var(--color-textTertiary)"
              fontSize="12"
            >
              {datum.label}
            </text>
          </g>
        ))}
      </svg>
      <figcaption className="visually-hidden">
        {data
          .map((datum) =>
            datum.series
              .map((series) =>
                t('a11y.chartBar', {
                  label: `${datum.label} ${series.label}`,
                  amount: formatValue(series.value),
                }),
              )
              .join('. '),
          )
          .join('. ')}
      </figcaption>
    </figure>
  )
}
